/**
 * 🔐 Auth0 Login Screen for Coach Cedar
 * Dark gradient design matching native app
 * Cross-platform support for web and native
 */

import React, { useEffect, useState } from 'react';
import { Platform, View, Text, TouchableOpacity, ScrollView, ActivityIndicator, Linking, Image, StyleSheet } from 'react-native';
import { useAuth0 } from '@auth0/auth0-react';
import { useUnifiedAuth0 as useUnifiedAuth } from '../auth/UnifiedAuth0Provider';
import { useLocation } from 'react-router-dom';
import { AlertCircle, Loader } from 'lucide-react';
import { auth0Connections } from '../auth/auth0-config';
import './Auth0LoginScreen.css';

// 2026-09-17 (hooks debt): the platform choice used to be made INSIDE the
// component — `Platform.OS === 'web' ? useAuth0() : {...}` — which is a hook
// behind a ternary. `Platform.OS` is fixed for the life of the JS process, so
// the same choice is made once, here, and the component calls the chosen hooks
// unconditionally. Web still gets useAuth0 + useLocation; native still gets
// useUnifiedAuth + a static location.
const usePlatformAuth: () => { loginWithRedirect: (...args: any[]) => any; isLoading: boolean; error: any } =
  Platform.OS === 'web' ? useAuth0 : useUnifiedAuth;
const NATIVE_LOCATION = { search: '', pathname: '/' };
const usePlatformLocation: () => { search: string; pathname: string } =
  Platform.OS === 'web' ? useLocation : () => NATIVE_LOCATION;

export const Auth0LoginScreen: React.FC = () => {
  // Use appropriate Auth0 hook based on platform
  const { loginWithRedirect, isLoading, error } = usePlatformAuth();

  // Platform-specific routing
  const location = usePlatformLocation();

  const [userFriendlyError, setUserFriendlyError] = useState<string | null>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const auth0Error = urlParams.get('error');
    const auth0ErrorDescription = urlParams.get('error_description');

    if (auth0Error) {
      let friendlyMessage = 'Authentication failed. Please try again.';
      if (auth0Error === 'invalid_request' && auth0ErrorDescription?.includes('user already exists')) {
        friendlyMessage = 'This email is already registered. Please sign in instead.';
      } else if (auth0ErrorDescription?.includes('already exists') || auth0ErrorDescription?.includes('user_exists')) {
        friendlyMessage = 'This email is already registered. Please sign in instead.';
      }

      setUserFriendlyError(friendlyMessage);
      setTimeout(() => {
        setUserFriendlyError(null);
        // 🚨 MISSION 20: Platform guard for window access
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
        }
      }, 8000);
    }
  }, [location.search]);

  const handleGoogleSignIn = () =>
    loginWithRedirect({
      authorizationParams: {
        connection: auth0Connections.google,
        prompt: 'select_account',
      },
    });

  const handleAppleSignIn = () =>
    loginWithRedirect({
      authorizationParams: {
        connection: auth0Connections.apple,
      },
    });

  const handleEmailSignIn = () =>
    loginWithRedirect({
      authorizationParams: {
        connection: auth0Connections.email,
        screen_hint: 'signin',
      },
    });

  const handleEmailSignUp = () =>
    loginWithRedirect({
      authorizationParams: {
        connection: auth0Connections.email,
        screen_hint: 'signup',
      },
    });

  const isNative = Platform.OS === 'android' || Platform.OS === 'ios';

  // Loading state - platform-specific
  if (isLoading) {
    if (isNative) {
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#1F6C4F" />
          <Text style={styles.loadingText}>Loading…</Text>
        </View>
      );
    }
    return (
      <div className="auth-loading">
        <div className="auth-loading-card">
          <Loader className="w-8 h-8 animate-spin text-green-600 mx-auto" />
          <p className="auth-loading-text">Loading…</p>
        </div>
      </div>
    );
  }

  // 🚨 MISSION 20/31: Native (Android/iOS) UI - Redesigned with centered layout and icon
  if (isNative) {
    return (
      <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
        {/* Coach Cedar Icon */}
        <Image
          source={require('../../assets/icon.png')}
          style={styles.icon}
          resizeMode="contain"
        />

        {/* Welcome Card - Cedar Subtle Background */}
        <View style={styles.welcomeCard}>
          <Text style={styles.welcomeTitle}>Hi, I'm Coach Cedar!</Text>
          <Text style={styles.welcomeSubtitle}>Welcome to the Coach Cedar Beta</Text>
        </View>

        {/* Error Display */}
        {(userFriendlyError || error) && (
          <View style={styles.errorCard}>
            <Text style={styles.errorText}>{userFriendlyError || (error?.message ?? '')}</Text>
          </View>
        )}

        {/* Sign-in Card - Always Visible */}
        <View style={styles.signInCard}>
          <Text style={styles.signInTitle}>Sign in</Text>
          
          {/* Continue with Google */}
          <TouchableOpacity
            style={styles.googleButton}
            onPress={handleGoogleSignIn}
            activeOpacity={0.8}
          >
            <Text style={styles.googleButtonText}>Continue with Google</Text>
          </TouchableOpacity>

          {/* Apple Sign In Button */}
          <TouchableOpacity
            style={styles.appleButton}
            onPress={handleAppleSignIn}
            activeOpacity={0.8}
          >
            <Text style={styles.appleButtonText}>Continue with Apple</Text>
          </TouchableOpacity>

          <View style={styles.divider}>
            <View style={styles.dividerLine} />
            <Text style={styles.dividerText}>or</Text>
            <View style={styles.dividerLine} />
          </View>

          {/* Sign in with Email */}
          <TouchableOpacity
            style={styles.emailButton}
            onPress={handleEmailSignIn}
            activeOpacity={0.8}
          >
            <Text style={styles.emailButtonText}>Sign in with Email</Text>
          </TouchableOpacity>

          {/* Create Account */}
          <TouchableOpacity
            style={styles.createButton}
            onPress={handleEmailSignUp}
            activeOpacity={0.8}
          >
            <Text style={styles.createButtonText}>Create Account</Text>
          </TouchableOpacity>
        </View>

        {/* Privacy Policy Link */}
        <TouchableOpacity
          style={styles.privacyLink}
          onPress={() => Linking.openURL('https://coachcedar.com/privacy')}
          activeOpacity={0.8}
        >
          <Text style={styles.privacyLinkText}>Privacy Policy</Text>
        </TouchableOpacity>
      </ScrollView>
    );
  }

  // 🚨 MISSION 20: Explicit web guard to prevent Android bundle from including HTML elements
  if (Platform.OS === 'web') {
    return (
      <div className="auth-shell">
        {/* Dark gradient hero — matches native LoginScreen */}
        <div className="auth-gradient-bg">

          {/* Logo — centered in upper section */}
          <div className="auth-logo-section">
            <img
              src="/CC_Logo_reverse_top.png"
              alt="Coach Cedar"
              className="auth-hero-logo"
            />
          </div>

          {/* Content — title, buttons, legal; stacked at bottom */}
          <div className="auth-content-section">

            {/* Welcome title */}
            <h1 className="auth-welcome-title">Welcome Back</h1>
            <p className="auth-welcome-subtitle">Login to your account</p>

            {/* Error display */}
            {userFriendlyError && (
              <div className="auth-alert" role="alert">
                <AlertCircle size={16} />
                <span>{userFriendlyError}</span>
              </div>
            )}

            {error && (
              <div className="auth-alert" role="alert">
                <AlertCircle size={16} />
                <span>{error.message}</span>
              </div>
            )}

            {/* Continue with Apple */}
            <button className="auth-apple-btn" onClick={handleAppleSignIn}>
              <svg className="auth-btn-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              Continue with Apple
            </button>

            {/* Continue with Google */}
            <button className="auth-google-btn" onClick={handleGoogleSignIn}>
              <svg className="auth-btn-icon" viewBox="0 0 24 24">
                <path fill="#EA4335" d="M5.27 9.76A7.08 7.08 0 0 1 12 5.48c1.73 0 3.3.6 4.52 1.59l3.38-3.38A11.95 11.95 0 0 0 12 0a12 12 0 0 0-10.7 6.52l3.97 3.24z" />
                <path fill="#4285F4" d="M23.5 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47a5.55 5.55 0 0 1-2.4 3.63l3.88 3.01c2.26-2.09 3.56-5.17 3.56-8.88z" />
                <path fill="#FBBC05" d="M5.26 14.27a7.13 7.13 0 0 1 0-4.54L1.29 6.5A12.01 12.01 0 0 0 0 12c0 1.94.47 3.77 1.29 5.5l3.97-3.23z" />
                <path fill="#34A853" d="M12 24c3.24 0 5.95-1.07 7.93-2.91l-3.87-3.01a7.18 7.18 0 0 1-4.06 1.16 7.07 7.07 0 0 1-6.73-4.97l-3.98 3.23A11.99 11.99 0 0 0 12 24z" />
              </svg>
              Continue with Google
            </button>

            {/* Divider */}
            <div className="auth-or-divider">
              <div className="auth-or-line" />
              <span className="auth-or-text">or</span>
              <div className="auth-or-line" />
            </div>

            {/* Sign in with Email */}
            <button className="auth-email-btn" onClick={handleEmailSignIn}>
              Sign in with Email
            </button>

            {/* Create account link */}
            <button className="auth-create-link" onClick={handleEmailSignUp}>
              Create account
            </button>

            {/* Legal disclaimer */}
            <p className="auth-legal">
              By signing in, you agree to our{' '}
              <a href="https://coachcedar.com/terms" target="_blank" rel="noopener noreferrer" className="auth-legal-link">Terms</a>
              {' '}and{' '}
              <a href="https://coachcedar.com/privacy" target="_blank" rel="noopener noreferrer" className="auth-legal-link">Privacy Policy</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Fallback: Should never reach here (both native and web conditions should be exhaustive)
  return null;
};

// 🎨 MISSION 10/20: React Native StyleSheet for native platforms
const styles = StyleSheet.create({
  // Loading state
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f0',
    padding: 24,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#113C2B',
  },

  // Main container
  container: {
    flex: 1,
    backgroundColor: '#f5f5f0',
  },
  contentContainer: {
    padding: 24,
    paddingBottom: 48,
    alignItems: 'center',
  },

  // Coach Cedar Icon
  icon: {
    width: 90,
    height: 90,
    marginTop: 40,
    marginBottom: 24,
  },

  // Welcome Card (Cedar Subtle)
  welcomeCard: {
    backgroundColor: '#E8F2ED', // cedar-subtle
    borderRadius: 12,
    padding: 20,
    width: '100%',
    maxWidth: 400,
    alignItems: 'center',
    marginBottom: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  welcomeTitle: {
    fontSize: 22,
    fontWeight: '600',
    color: '#113C2B', // cedar-dark for readability
    marginBottom: 8,
    textAlign: 'center',
  },
  welcomeSubtitle: {
    fontSize: 16,
    color: '#113C2B',
    textAlign: 'center',
  },

  // Error Card
  errorCard: {
    marginBottom: 16,
    padding: 12,
    backgroundColor: '#ffebee',
    borderRadius: 8,
    width: '100%',
    maxWidth: 400,
  },
  errorText: {
    fontSize: 14,
    color: '#c62828',
    textAlign: 'center',
  },

  // Sign-in Card
  signInCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 20,
    width: '100%',
    maxWidth: 400,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  signInTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#113C2B',
    marginBottom: 16,
    textAlign: 'center',
  },

  // Google Button
  googleButton: {
    backgroundColor: '#1F6C4F', // cedar-primary
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 12,
  },
  googleButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },

  // Apple Button
  appleButton: {
    backgroundColor: '#000000',
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 12,
  },
  appleButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },

  // Divider
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 16,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#ddd',
  },
  dividerText: {
    marginHorizontal: 12,
    fontSize: 14,
    color: '#666',
  },

  // Email Button
  emailButton: {
    backgroundColor: '#eee',
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 12,
  },
  emailButtonText: {
    color: '#113C2B',
    fontSize: 16,
  },

  // Create Account Button
  createButton: {
    backgroundColor: '#113C2B', // cedar-dark
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
  },
  createButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },

  // Privacy Link
  privacyLink: {
    marginTop: 24,
    paddingVertical: 12,
  },
  privacyLinkText: {
    fontSize: 14,
    color: '#1F6C4F',
    textAlign: 'center',
  },
});
